import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { RootState } from '../store'
import {
  BookOpenIcon,
  ClockIcon,
  CalendarIcon,
  PlayIcon,
  CheckCircleIcon,
  TrophyIcon,
  LightBulbIcon,
  ArrowRightIcon,
  PlusIcon,
  CpuChipIcon,
  HeartIcon,
  BoltIcon,
  ArrowPathIcon,
  ClockIcon as TimerIcon,
  ChartPieIcon
} from '@heroicons/react/24/outline'

const StudyModule: React.FC = () => {
  const navigate = useNavigate()
  // const dispatch = useDispatch()
  const { sessions } = useSelector((state: RootState) => state.study)
  
  const [currentTime, setCurrentTime] = useState(new Date())
  const [studyStreak] = useState(7) // Örnek veri
  // const [todayStudyTime, setTodayStudyTime] = useState(145) // dakika
  
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  const getGreeting = () => {
    const hour = currentTime.getHours()
    if (hour < 12) return 'Günaydın'
    if (hour < 18) return 'İyi öğleden sonra'
    return 'İyi akşamlar'
  }

  // Örnek deste verileri
  const sampleDecks = [
    {
      id: 1,
      title: "İngilizce Kelimeler",
      description: "Temel İngilizce kelime hazinesi geliştirme destesi",
      subject: "İngilizce",
      totalCards: 150,
      masteredCards: 89,
      color: "from-blue-500 to-indigo-600"
    },
    {
      id: 2,
      title: "Matematik Formülleri",
      description: "Lise matematik formülleri ve teoremler",
      subject: "Matematik",
      totalCards: 75,
      masteredCards: 45,
      color: "from-green-500 to-emerald-600"
    },
    {
      id: 3,
      title: "Tarih Olayları",
      description: "Osmanlı İmparatorluğu dönemi önemli olaylar",
      subject: "Tarih",
      totalCards: 120,
      masteredCards: 67,
      color: "from-purple-500 to-pink-600"
    }
  ];

  // Mevcut desteler varsa onları kullan, yoksa örnek verileri kullan
  const activeDecks: any[] = sampleDecks;



  // Çalışma modları
  const studyModes = [
    {
      id: 'review',
      title: 'Tekrar Kartları',
      description: 'Tekrar edilmesi gereken kartlar',
      icon: ClockIcon,
      color: 'from-blue-500 to-blue-600',
      count: 0,
      action: () => {}
    },
    {
      id: 'random',
      title: 'Karışık Çalışma',
      description: 'Rastgele kart seçimi',
      icon: ArrowPathIcon,
      color: 'from-green-500 to-green-600',
      count: 0,
      action: () => {}
    },
    {
      id: 'difficult',
      title: 'Zor Kartlar',
      description: 'Zorlandığınız kartlar',
      icon: BoltIcon,
      color: 'from-red-500 to-red-600',
      count: 0,
      action: () => {}
    },
    {
      id: 'favorites',
      title: 'Favori Kartlar',
      description: 'Favori olarak işaretlenen kartlar',
      icon: HeartIcon,
      color: 'from-pink-500 to-pink-600',
      count: 0,
      action: () => {}
    }
  ]

  const quickActions = [
    {
      title: 'Notlar',
      description: 'Notlarınızı yönetin',
      icon: CpuChipIcon,
      color: 'from-purple-500 to-purple-600',
      path: '/tools/notes',
      count: 0
    },
    {
      title: 'Yeni Not Oluştur',
      description: 'Yeni not ekleyin',
      icon: PlusIcon,
      color: 'from-indigo-500 to-indigo-600',
      path: '/tools/notes',
      count: 0
    },
    {
      title: 'İstatistikler',
      description: 'Çalışma performansınızı görün',
      icon: ChartPieIcon,
      color: 'from-orange-500 to-orange-600',
      path: '/analytics/performance',
      count: 0
    },
    {
      title: 'Çalışma Geçmişi',
      description: 'Geçmiş çalışma oturumları',
      icon: TimerIcon,
      color: 'from-teal-500 to-teal-600',
      path: '/study/sessions',
      count: sessions.length
    }
  ]

  const recentActivities = [
    {
      type: 'study',
      title: 'Matematik çalışması tamamlandı',
      time: '2 saat önce',
      duration: '45 dakika'
    },
    {
      type: 'study',
      title: 'Tarih kartları çalışıldı',
      time: '4 saat önce',
      duration: '20 dakika'
    },
    {
      type: 'plan',
      title: 'Yeni çalışma planı oluşturuldu',
      time: '1 gün önce',
      duration: null
    }
  ]

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Welcome Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 text-white relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6">
            <div className="mb-3 sm:mb-0">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-1 sm:mb-2">
                {getGreeting()}! 🎯
              </h1>
              <p className="text-sm sm:text-base lg:text-lg text-white/90">
                Çalışmaya başlayın!
              </p>
            </div>
            <div className="text-left sm:text-right">
              <p className="text-xs sm:text-sm text-white/80">Bugün</p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold">0</p>
              <p className="text-xs sm:text-sm text-white/80">dakika çalıştınız</p>
            </div>
          </div>

          {/* Study Stats Row */}
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="p-1.5 sm:p-2 bg-white/20 rounded-lg flex-shrink-0">
                  <CpuChipIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-white/80">Toplam Not</p>
                  <p className="text-sm sm:text-lg lg:text-xl font-bold">0</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="p-1.5 sm:p-2 bg-white/20 rounded-lg flex-shrink-0">
                  <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-white/80">Öğrenilen</p>
                  <p className="text-sm sm:text-lg lg:text-xl font-bold">0</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="p-1.5 sm:p-2 bg-white/20 rounded-lg flex-shrink-0">
                  <ClockIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-white/80">Tekrar Bekleyen</p>
                  <p className="text-sm sm:text-lg lg:text-xl font-bold">0</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="p-1.5 sm:p-2 bg-white/20 rounded-lg flex-shrink-0">
                  <TrophyIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-white/80">Doğruluk</p>
                  <p className="text-sm sm:text-lg lg:text-xl font-bold">0%</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Study Modes */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <PlayIcon className="w-6 h-6 text-blue-600" />
          Çalışma Modları
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {studyModes.map((mode, index: number) => (
            <motion.div
              key={mode.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={mode.action}
              className="group cursor-pointer"
            >
              <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all duration-300 group-hover:scale-105">
                <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r ${mode.color} rounded-lg flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform`}>
                  <mode.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white mb-2">
                  {mode.title}
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3">
                  {mode.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                    {mode.count}
                  </span>
                  <ArrowRightIcon className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4 flex items-center gap-2">
          <BoltIcon className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
          Hızlı Erişim
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {quickActions.map((action, index: number) => (
            <motion.div
              key={action.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => navigate(action.path)}
              className="group cursor-pointer"
            >
              <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all duration-300 group-hover:scale-105">
                <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r ${action.color} rounded-lg flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform`}>
                  <action.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white mb-2">
                  {action.title}
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3">
                  {action.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 dark:text-gray-500">
                    {action.count > 0 ? `${action.count} öğe` : 'Yeni'}
                  </span>
                  <ArrowRightIcon className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Recent Decks */}
      <div>
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <CpuChipIcon className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
            Son Çalışılan Desteler
          </h2>
          <button
            onClick={() => navigate('/tools/notes')}
            className="text-blue-600 hover:text-blue-700 text-xs sm:text-sm font-medium"
          >
            Tümünü Gör
          </button>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {activeDecks.slice(0, 3).map((deck, index: number) => (
            <motion.div
              key={deck.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => navigate('/tools/notes')}
              className="group cursor-pointer"
            >
              <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center bg-gradient-to-r ${deck.color || 'from-blue-500 to-purple-600'}`}>
                    <CpuChipIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-gray-600 dark:text-gray-400">
                    {deck.subject}
                  </span>
                </div>
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-blue-600 transition-colors">
                  {deck.title}
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3 sm:mb-4 line-clamp-2">
                  {deck.description}
                </p>
                <div className="grid grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-500">Toplam:</span>
                    <span className="font-semibold text-gray-900 dark:text-white ml-1">
                      {deck.totalCards}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-500">Öğrenilen:</span>
                    <span className="font-semibold text-green-600 ml-1">
                      {deck.masteredCards}
                    </span>
                  </div>
                </div>
                <div className="mt-3 sm:mt-4">
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 sm:h-2">
                    <div 
                      className="bg-gradient-to-r from-green-500 to-emerald-500 h-1.5 sm:h-2 rounded-full transition-all duration-500"
                      style={{ width: `${deck.totalCards > 0 ? (deck.masteredCards / deck.totalCards) * 100 : 0}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    {deck.totalCards > 0 ? Math.round((deck.masteredCards / deck.totalCards) * 100) : 0}% tamamlandı
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Study Progress */}
      <div>
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4 flex items-center gap-2">
          <ChartPieIcon className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
          Çalışma İlerlemesi
        </h2>
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg sm:rounded-xl p-4 sm:p-6 text-white">
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div className="bg-white/10 rounded-lg p-3 sm:p-4 text-center">
              <h4 className="text-sm sm:text-base font-semibold mb-1 sm:mb-2">Bugün</h4>
              <p className="text-xl sm:text-2xl font-bold">0</p>
              <p className="text-xs sm:text-sm text-green-100">kart çalışıldı</p>
            </div>
            <div className="bg-white/10 rounded-lg p-3 sm:p-4 text-center">
                <h4 className="text-sm sm:text-base font-semibold mb-1 sm:mb-2">Bu Hafta</h4>
                <p className="text-xl sm:text-2xl font-bold">0</p>
                <p className="text-xs sm:text-sm text-green-100">kart çalışıldı</p>
              </div>
            <div className="bg-white/10 rounded-lg p-3 sm:p-4 text-center">
              <h4 className="text-sm sm:text-base font-semibold mb-1 sm:mb-2">Çalışma Serisi</h4>
              <p className="text-xl sm:text-2xl font-bold">{studyStreak}</p>
              <p className="text-xs sm:text-sm text-green-100">gün</p>
            </div>
            <div className="bg-white/10 rounded-lg p-3 sm:p-4 text-center">
              <h4 className="text-sm sm:text-base font-semibold mb-1 sm:mb-2">Doğruluk</h4>
              <p className="text-xl sm:text-2xl font-bold">0%</p>
              <p className="text-xs sm:text-sm text-green-100">ortalama</p>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Goals */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <CalendarIcon className="w-6 h-6 text-indigo-600" />
          Günlük Hedefler
        </h2>
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white/10 rounded-lg p-4 text-center">
              <h4 className="font-semibold mb-2">Yeni Kartlar</h4>
              <div className="flex items-center justify-center gap-2">
                <p className="text-2xl font-bold">8</p>
                <span className="text-sm">/</span>
                <p className="text-lg">15</p>
              </div>
              <div className="w-full bg-white/20 rounded-full h-2 mt-2">
                <div className="bg-white h-2 rounded-full" style={{ width: '53%' }} />
              </div>
            </div>
            <div className="bg-white/10 rounded-lg p-4 text-center">
              <h4 className="font-semibold mb-2">Tekrar Kartları</h4>
              <div className="flex items-center justify-center gap-2">
                <p className="text-2xl font-bold">23</p>
                <span className="text-sm">/</span>
                <p className="text-lg">30</p>
              </div>
              <div className="w-full bg-white/20 rounded-full h-2 mt-2">
                <div className="bg-white h-2 rounded-full" style={{ width: '77%' }} />
              </div>
            </div>
            <div className="bg-white/10 rounded-lg p-4 text-center">
              <h4 className="font-semibold mb-2">Çalışma Süresi</h4>
              <div className="flex items-center justify-center gap-2">
                <p className="text-2xl font-bold">25</p>
                <span className="text-sm">/</span>
                <p className="text-lg">45</p>
              </div>
              <div className="w-full bg-white/20 rounded-full h-2 mt-2">
                <div className="bg-white h-2 rounded-full" style={{ width: '56%' }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Study Sessions */}
      <div>
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <ClockIcon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            Son Çalışma Oturumları
          </h2>
          <button
            onClick={() => navigate('/study/sessions')}
            className="text-blue-600 hover:text-blue-700 text-xs sm:text-sm font-medium"
          >
            Tümünü Gör
          </button>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          {recentActivities.map((activity) => (
            <div
              key={activity.title}
              className="p-3 sm:p-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    activity.type === 'study' ? 'bg-blue-100 dark:bg-blue-900/30' :
                    activity.type === 'study' ? 'bg-purple-100 dark:bg-purple-900/30' :
                    'bg-green-100 dark:bg-green-900/30'
                  }`}>
                    {activity.type === 'study' && <BookOpenIcon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />}
                    {activity.type === 'study' && <CpuChipIcon className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 dark:text-purple-400" />}
                    {activity.type === 'plan' && <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 dark:text-green-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm sm:text-base font-medium text-gray-900 dark:text-white truncate">
                      {activity.title}
                    </p>
                    <div className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm text-gray-500 dark:text-gray-500">
                      <span className="truncate">{activity.time}</span>
                      {activity.duration && (
                        <>
                          <span>•</span>
                          <span className="truncate">{activity.duration}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                  <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                    {Math.floor(Math.random() * 30) + 15} kart
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    {Math.floor(Math.random() * 20) + 10} dakika
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Study Tips */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <LightBulbIcon className="w-6 h-6 text-yellow-600" />
          Günün İpucu
        </h2>
        <div className="bg-gradient-to-r from-yellow-500 to-orange-600 rounded-xl p-6 text-white">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <LightBulbIcon className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">Aralıklı Tekrar Sistemi</h3>
              <p className="text-yellow-100">
                Notlarınızı düzenli aralıklarla tekrar edin. Kolay öğrendiğiniz konuları daha az, 
                zor öğrendiklerinizi daha sık tekrar ederek hafızanızı güçlendirin. 
                Bu sistem uzun vadeli öğrenme için en etkili yöntemdir.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StudyModule